import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { authService, UserData, AthleteUserData } from '../services/authService';

interface AuthContextType {
  user: UserData | null;
  token: string | null;
  loading: boolean;
  login: (token: string, user: UserData) => void;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  isAthlete: boolean;
  isCompany: boolean;
  mirailogLinked: boolean;
  mirailogAthleteId: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const TOKEN_KEY = 'auth_token';

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<UserData | null>(null);
  const [token, setToken] = useState<string | null>(() => localStorage.getItem(TOKEN_KEY));
  const [loading, setLoading] = useState(true);

  // axios interceptor: Authorizationヘッダー付与
  useEffect(() => {
    const reqInterceptor = axios.interceptors.request.use((config) => {
      const storedToken = localStorage.getItem(TOKEN_KEY);
      if (storedToken) {
        config.headers.Authorization = `Bearer ${storedToken}`;
      }
      return config;
    });

    // 401レスポンスインターセプター: トークン期限切れ時に自動ログアウト
    const resInterceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401 && localStorage.getItem(TOKEN_KEY)) {
          localStorage.removeItem(TOKEN_KEY);
          setToken(null);
          setUser(null);
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.request.eject(reqInterceptor);
      axios.interceptors.response.eject(resInterceptor);
    };
  }, []);

  // 初回ロード: トークンがあればユーザー情報取得
  useEffect(() => {
    const init = async () => {
      if (!token) {
        setLoading(false);
        return;
      }
      try {
        const res = await authService.getMe();
        setUser(res.data);
      } catch {
        // トークン無効 → クリア
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [token]);

  const login = useCallback((newToken: string, userData: UserData) => {
    localStorage.setItem(TOKEN_KEY, newToken);
    setToken(newToken);
    setUser(userData);
  }, []);

  const logout = useCallback(async () => {
    try {
      await authService.logout();
    } catch {
      // ログアウトAPI失敗してもローカルはクリア
    }
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setUser(null);
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const res = await authService.getMe();
      setUser(res.data);
    } catch {
      // 無視
    }
  }, []);

  const isAthlete = user?.userType === 'athlete';
  const isCompany = user?.userType === 'company';
  const mirailogLinked = isAthlete && !!(user as AthleteUserData)?.mirailogLinked;
  const mirailogAthleteId = isAthlete ? (user as AthleteUserData)?.mirailogAthleteId || null : null;

  return (
    <AuthContext.Provider value={{ user, token, loading, login, logout, refreshUser, isAthlete, isCompany, mirailogLinked, mirailogAthleteId }}>
      {children}
    </AuthContext.Provider>
  );
};

export function useAuth(): AuthContextType {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}
